import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  BuildState,
  BuildActions,
  BuildPlatform,
  BuildResult,
  DEFAULT_BUILD_SETTINGS,
  BUILD_STAGES,
} from '../../core/types/build.types';

interface BuildStore extends BuildState, BuildActions {}

export const useBuildStore = create<BuildStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      platform: 'web',
      settings: DEFAULT_BUILD_SETTINGS,
      isBuilding: false,
      progress: {
        stage: '',
        progress: 0,
        message: '',
      },
      lastBuild: null,
      buildHistory: [],
      isOpen: false,
      showAdvanced: false,
      activeTab: 'settings',

      // Dialog
      openDialog: () => {
        set((state) => {
          state.isOpen = true;
        });
      },

      closeDialog: () => {
        set((state) => {
          state.isOpen = false;
        });
      },

      // Settings
      setPlatform: (platform) => {
        set((state) => {
          state.platform = platform;
        });
      },

      updateSettings: (settings) => {
        set((state) => {
          state.settings = { ...state.settings, ...settings };
        });
      },

      resetSettings: () => {
        set((state) => {
          state.settings = DEFAULT_BUILD_SETTINGS;
        });
      },

      // Build
      startBuild: async () => {
        const { platform, settings } = get();

        set((state) => {
          state.isBuilding = true;
          state.progress = {
            stage: BUILD_STAGES[0].id,
            progress: 0,
            message: BUILD_STAGES[0].label,
          };
        });

        const startTime = Date.now();
        const errors: string[] = [];
        const warnings: string[] = [];

        try {
          // Simulate build process (in real implementation, this would call Electron IPC)
          for (let i = 0; i < BUILD_STAGES.length; i++) {
            const stage = BUILD_STAGES[i];

            set((state) => {
              state.progress = {
                stage: stage.id,
                progress: (i / BUILD_STAGES.length) * 100,
                message: stage.label,
              };
            });

            // Simulate stage duration
            await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 500));

            // Simulate sub-progress
            for (let p = 0; p < 10; p++) {
              set((state) => {
                state.progress.progress = ((i + (p / 10)) / BUILD_STAGES.length) * 100;
              });
              await new Promise((resolve) => setTimeout(resolve, 50));
            }
          }

          // Build complete
          const buildTime = Date.now() - startTime;
          const result: BuildResult = {
            success: true,
            platform,
            outputPath: settings.outputDirectory,
            files: [
              { path: 'index.html', name: 'index.html', type: 'html', size: 2048, compressed: true },
              { path: 'bundle.js', name: 'bundle.js', type: 'javascript', size: 512000, compressed: true },
              { path: 'assets/', name: 'assets', type: 'folder', size: 1024000, compressed: true },
            ],
            totalSize: 1538048,
            buildTime,
            errors,
            warnings,
          };

          set((state) => {
            state.isBuilding = false;
            state.lastBuild = result;
            state.buildHistory.unshift(result);
            if (state.buildHistory.length > 10) {
              state.buildHistory = state.buildHistory.slice(0, 10);
            }
            state.progress = {
              stage: 'complete',
              progress: 100,
              message: 'Build complete!',
            };
          });

          return result;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown build error';
          errors.push(errorMessage);

          const result: BuildResult = {
            success: false,
            platform,
            outputPath: settings.outputDirectory,
            files: [],
            totalSize: 0,
            buildTime: Date.now() - startTime,
            errors,
            warnings,
          };

          set((state) => {
            state.isBuilding = false;
            state.lastBuild = result;
            state.progress = {
              stage: 'error',
              progress: 0,
              message: errorMessage,
            };
          });

          return result;
        }
      },

      cancelBuild: () => {
        set((state) => {
          state.isBuilding = false;
          state.progress = {
            stage: 'cancelled',
            progress: 0,
            message: 'Build cancelled',
          };
        });
      },

      // UI
      setActiveTab: (tab) => {
        set((state) => {
          state.activeTab = tab;
        });
      },

      toggleAdvanced: () => {
        set((state) => {
          state.showAdvanced = !state.showAdvanced;
        });
      },

      // History
      clearHistory: () => {
        set((state) => {
          state.buildHistory = [];
        });
      },
    })),
    {
      name: 'build-store',
      partialize: (state) => ({
        settings: state.settings,
        platform: state.platform,
        buildHistory: state.buildHistory.slice(0, 5),
      }),
    }
  )
);
